import { select } from 'd3-selection';
import { annotation, annotationLabel } from 'd3-svg-annotation';

export class SwotRenderer {
    constructor ({
        chartDataHelper,
        chartRenderer,
        chartInteractions,
        scaler,
        canvas
    }) {
        this.canvas = canvas;
        this.chartDataHelper = chartDataHelper;
        this.chartRenderer = chartRenderer;
        this.interactions = chartInteractions;
        this.scaler = scaler;
        this.context = {
            margin: {
                left: 40, right: 35, top: 15, bottom: 40
            },
            palette: {
                fill: '#ff0000',
                stroke: '#444444',
                lineMain: 'black',
                lineAlternate: 'lightgray',
                annotation: '#167df0'
            },
            r: {
                min: 5,
                max: 20
            }
        };

        this.interactions.onModuleToggleHelp(this.chartDataHelper.type,
            this._toggleHelp.bind(this));
    }

    render (mode) {
        let yAxisLabel = this.chartDataHelper.settings.yAxis.label;
        let xAxisLabel = this.chartDataHelper.settings.xAxis.label;

        this.chartRenderer.addAxisLines(this.scaler.axis);
        this.chartRenderer.addAxisLabels(this.context, xAxisLabel, yAxisLabel);
        this._addGridLines(10, 10);
        this._addGridLabels('Participant', 'Leader', 'Wallflower', 'Self-Talker');

        this.chartRenderer.getMainZone()
            .selectAll('.series')
            .data(this.chartDataHelper.dataSeries, d => d.series)
            .join(
                enter => enter.append('g').attr('class', 'series')
            )
            .each((d, i, g) => {
                const seriesId = d.series;
                select(g[i])
                    .selectAll('circle')
                    .data(d => d.metric)
                    .join(
                        enter => enter
                            .append('circle')
                            .attr('stroke', this.context.palette.stroke)
                            .attr('cx', d => this.scaler.x(d[0]) + this.context.margin.left)
                            .attr('cy', d => this.scaler.y(d[1]) + this.context.margin.top)
                            .on('mouseover', d => {
                                this.interactions.emitOverSeries({ seriesId, data: d, event });
                            })
                            .on('mouseout', d => {
                                this.interactions.emitOutSeries({ seriesId, data: d, event });
                            })
                            .on('click', d => {
                                this.interactions.emitClickSeries({ seriesId, data: d, event });
                            })
                    )
                    .transition()
                    .attr('fill', this.chartDataHelper.getSeriesColour(seriesId))
                    .attr('cx', d => this.scaler.x(d[0]) + this.context.margin.left)
                    .attr('cy', d => this.scaler.y(d[1]) + this.context.margin.top)
                    .attr('r', d => this.scaler.z(d[2]));
            });
    }

    _addGridLabels (label1, label2, label3, label4) {
        let container = this.canvas.container.select('.gridLabels');
        if (container.empty()) {
            container = this.canvas.container.append('g')
                .attr('class', 'gridLabels');
        }
        container.selectAll('text.gridLabel').remove();

        if (this.context.zoom) {
            // hide labels when zoom in
            return;
        }

        const commonAttributes = {
            fontSize: 12
        };
        for (const el of [
            {
                label: label1,
                x: this.context.margin.left + 5,
                y: this.context.margin.top + 13,
                anchor: 'left'
            },
            {
                label: label2,
                x: this.canvas.width - this.context.margin.right - this.context.margin.left - 2,
                y: this.context.margin.top + 13,
                anchor: 'right'
            },
            {
                label: label3,
                x: this.context.margin.left + 5,
                y: this.canvas.height - this.context.margin.bottom - this.context.margin.top + 10,
                anchor: 'left'
            },
            {
                label: label4,
                x: this.canvas.width - this.context.margin.right - this.context.margin.left - 22,
                y: this.canvas.height - this.context.margin.bottom - this.context.margin.top + 10,
                anchor: 'right'
            }
        ]) {
            container.append('text')
                .attr('font-size', commonAttributes.fontSize)
                .attr('x', el.x)
                .attr('y', el.y)
                .attr('class', 'gridLabel')
                .style('text-anchor', 'left')
                .style('fill', this.context.palette.stroke)
                .text(el.label);
        }
    }

    _addGridLines (hCount, vCount) {
        let container = this.canvas.container.select('.gridLines');
        if (container.empty()) {
            container = this.canvas.container.append('g')
                .attr('class', 'gridLines')
                .attr('opacity', 0.4);
        }
        const hIndexes = [];
        for (let index = 0; index < hCount; index++) {
            hIndexes.push(index);
        }
        const hInterval = 100 / hCount;
        container.selectAll('.hline')
            .data(hIndexes)
            .join(
                enter => enter
                    .append('line')
                    .attr('class', 'hline')
                    .attr('stroke', d => (d + 1) % 5 ? this.context.palette.lineAlternate : this.context.palette.lineMain)
                    .attr('stroke-width', 1)
            )
            .transition()
            .attr('x1', d => this.scaler.x(0) + this.context.margin.left + 0.5)
            .attr('y1', d => this.scaler.y((d + 1) * hInterval) + this.context.margin.top + 0.5)
            .attr('x2', d => this.scaler.x(100) + this.context.margin.left + 0.5)
            .attr('y2', d => this.scaler.y((d + 1) * hInterval) + this.context.margin.top + 0.5);
        const vIndexes = [];
        for (let index = 0; index < vCount; index++) {
            vIndexes.push(index);
        }
        const vInterval = 100 / vCount;
        container.selectAll('.vline')
            .data(vIndexes)
            .join(
                enter => enter
                    .append('line')
                    .attr('class', 'vline')
                    .attr('stroke', d => (d + 1) % 5 ? this.context.palette.lineAlternate : this.context.palette.lineMain)
                    .attr('stroke-width', 1)
            )
            .transition()
            .attr('x1', d => this.scaler.x((d + 1) * vInterval) + this.context.margin.left + 0.5)
            .attr('y1', d => this.scaler.y(0) + this.context.margin.top + 0.5)
            .attr('x2', d => this.scaler.x((d + 1) * vInterval) + this.context.margin.left + 0.5)
            .attr('y2', d => this.scaler.y(100) + this.context.margin.top + 0.5);
    }

    _toggleHelp (enabled) {
        if (enabled) {
            const posWidth = this.canvas.width - this.context.margin.left - this.context.margin.right;
            const posHeight = this.canvas.height - this.context.margin.top - this.context.margin.bottom;
            const wrap = (this.canvas.width - this.context.margin.left - this.context.margin.right) / 2 - 20;
            const commonNote = {
                wrap,
                align: 'left'
            };
            const commonAnnotation = {
                color: this.context.palette.annotation
            };
            const annotations = [
                {
                    title: 'Participant',
                    label: 'Brand is not talking much, but the quality of the content is good',
                    x: this.context.margin.left + 10,
                    y: this.context.margin.top + posHeight / 4 - 26
                },
                {
                    title: 'Leader',
                    label: 'Brand has a lot of content and people are responding to it',
                    x: this.context.margin.left + posWidth / 2 + 10,
                    y: this.context.margin.top + posHeight / 4 - 26
                },
                {
                    title: 'Self-Talker',
                    label: 'Brand has a lot of content but no one is responding to it',
                    x: this.context.margin.left + posWidth / 2 + 10,
                    y: this.context.margin.top + posHeight / 4 + posHeight / 2 - 26
                },
                {
                    title: 'Wallflower',
                    label: 'Brand doesn\'t have much content and people are not responding to it',
                    x: this.context.margin.left + 10,
                    y: this.context.margin.top + posHeight / 4 + posHeight / 2 - 26
                }
            ];
            const makeAnnotations = annotation()
                .type(annotationLabel)
                .annotations(annotations.map(n => ({
                    note: {
                        title: n.title,
                        label: n.label,
                        ...commonNote
                    },
                    x: n.x,
                    y: n.y,
                    ...commonAnnotation
                })));
            // hide bubbles
            this.chartRenderer.getMainZone()
                .selectAll('circle')
                .transition()
                .style('fill-opacity', 0)
                .style('stroke-opacity', 0);
            // show annotations
            this.chartRenderer.getMainZone()
                .append('g')
                .attr('class', 'annotation-group')
                .attr('opacity', 0)
                .call(makeAnnotations)
                .transition()
                .delay(300)
                .attr('opacity', 1);
        } else {
            // show bubbles
            this.chartRenderer.getMainZone()
                .selectAll('circle')
                .transition()
                .style('fill-opacity', 1)
                .style('stroke-opacity', 1);
            // hide annotations
            this.chartRenderer.getMainZone()
                .selectAll('.annotation-group')
                .remove();
        }
    }
}
